import { z } from 'zod';

/**
 * Backup vocabulary shared by the legacy Coolify database integration
 * (ADR-0015) and restore-tested subscription protection (ADR-0042).
 *
 * Honest Coolify boundaries encoded by the legacy half (verified against
 * Coolify v4.1.2):
 * - Coolify schedules and runs the backups; the panel holds no backup
 *   state of its own beyond per-plan default policies and the registered
 *   S3 destinations.
 * - There is NO restore endpoint and NO backup-download endpoint in
 *   Coolify's API — restores are a documented manual procedure
 *   (docs/backup-restore-runbook.md).
 * - S3 destinations cannot be created or even listed through the API;
 *   admins register the uuids of storages created in Coolify's UI so they
 *   become pickable per backup. Reads only ever echo Coolify's internal
 *   `s3_storage_id` integer — the uuid cannot be recovered from the API,
 *   so schedule DTOs carry the opaque id.
 * - "Backup now" is not an endpoint: it is a `backup_now` body flag on the
 *   create/update calls that dispatches a run.
 * - Only standalone databases are covered; databases embedded in one-click
 *   services have no backup API (they 404 on these routes).
 * - Coolify's backup job only dumps PostgreSQL, MySQL, MariaDB and MongoDB
 *   (pg_dump / mysqldump / mariadb-dump / mongodump). The other four
 *   engines (redis, clickhouse, dragonfly, keydb) accept no schedules.
 */

/** Engines Coolify's DatabaseBackupJob can actually dump. */
export const BACKUP_SUPPORTED_ENGINES = ['postgresql', 'mysql', 'mariadb', 'mongodb'] as const;

/**
 * Whether scheduled backups exist for a database engine. An unknown or
 * missing engine (e.g. adopted resources without ledger details) answers
 * true — Coolify is the authority then, not the panel.
 */
export function engineSupportsBackups(engine: unknown): boolean {
  if (typeof engine !== 'string' || engine.length === 0) return true;
  return (BACKUP_SUPPORTED_ENGINES as readonly string[]).includes(engine);
}

// ── Frequency ───────────────────────────────────────────────────────

/** Named presets Coolify accepts anywhere a cron expression is valid. */
export const BACKUP_FREQUENCY_PRESETS = [
  'every_minute',
  'hourly',
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const;
export type BackupFrequencyPreset = (typeof BACKUP_FREQUENCY_PRESETS)[number];

/** What each preset means (Coolify's VALID_CRON_STRINGS), for UI hints. */
export const BACKUP_FREQUENCY_PRESET_CRON: Record<BackupFrequencyPreset, string> = {
  every_minute: '* * * * *',
  hourly: '0 * * * *',
  daily: '0 0 * * *',
  weekly: '0 0 * * 0',
  monthly: '0 0 1 * *',
  yearly: '0 0 1 1 *',
};

/**
 * One field of a five-field cron expression: numbers, ranges, steps,
 * lists and `*` — deliberately conservative (no names like MON/JAN);
 * Coolify validates authoritatively, this only catches typos early.
 */
const CRON_FIELD = /^(\*|\d+(-\d+)?)(\/\d+)?(,(\*|\d+(-\d+)?)(\/\d+)?)*$/;
const CRON_RANGES = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 6],
] as const;

function isValidCronField(field: string, min: number, max: number): boolean {
  if (!CRON_FIELD.test(field)) return false;
  return field.split(',').every((part) => {
    const [base = '*', rawStep] = part.split('/');
    if (rawStep !== undefined) {
      const step = Number(rawStep);
      if (!Number.isInteger(step) || step <= 0) return false;
    }
    if (base === '*') return true;
    const [rawStart, rawEnd] = base.split('-');
    const start = Number(rawStart);
    const end = rawEnd === undefined ? start : Number(rawEnd);
    return (
      Number.isInteger(start)
      && Number.isInteger(end)
      && start >= min
      && end <= max
      && start <= end
    );
  });
}

export function isValidBackupFrequency(value: string): boolean {
  // Coolify also accepts @-prefixed preset spellings (@daily, @weekly, …).
  const bare = value.startsWith('@') ? value.slice(1) : value;
  if ((BACKUP_FREQUENCY_PRESETS as readonly string[]).includes(bare)) return true;
  const fields = value.trim().split(/\s+/);
  return (
    fields.length === 5
    && fields.every((field, index) => {
      const [min, max] = CRON_RANGES[index]!;
      return isValidCronField(field!, min, max);
    })
  );
}

export const backupFrequencySchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine(isValidBackupFrequency, {
    message:
      'Frequency must be a named preset (every_minute, hourly, daily, weekly, monthly, yearly) ' +
      'or a five-field cron expression',
  });

// ── Schedule create / update ────────────────────────────────────────

/**
 * Retention knobs mirror Coolify's, locally and on S3 independently:
 * keep at most N files (count), delete files older than D days, cap total
 * size at S **gigabytes** (Coolify's unit; fractions allowed). 0 means
 * "no limit of this kind" — Coolify's default.
 */
const retentionCountSchema = z.number().int().min(0).max(100_000);
const retentionGbSchema = z.number().min(0).max(1_000_000);

const scheduleShape = {
  frequency: backupFrequencySchema,
  enabled: z.boolean(),
  /** Also upload dumps to an S3 storage registered in the panel settings. */
  saveS3: z.boolean(),
  s3StorageUuid: z.string().trim().min(1).max(100).nullish(),
  /** PostgreSQL only: dump all databases in the instance with pg_dumpall. */
  dumpAll: z.boolean(),
  retentionAmountLocally: retentionCountSchema,
  retentionDaysLocally: retentionCountSchema,
  retentionMaxStorageGbLocally: retentionGbSchema,
  retentionAmountS3: retentionCountSchema,
  retentionDaysS3: retentionCountSchema,
  retentionMaxStorageGbS3: retentionGbSchema,
};

const s3Rule = (data: { saveS3?: boolean; s3StorageUuid?: string | null }) =>
  !data.saveS3 || (typeof data.s3StorageUuid === 'string' && data.s3StorageUuid.length > 0);

export const createBackupScheduleSchema = z
  .object({
    ...scheduleShape,
    enabled: scheduleShape.enabled.default(true),
    saveS3: scheduleShape.saveS3.default(false),
    dumpAll: scheduleShape.dumpAll.default(false),
    retentionAmountLocally: retentionCountSchema.default(0),
    retentionDaysLocally: retentionCountSchema.default(0),
    retentionMaxStorageGbLocally: retentionGbSchema.default(0),
    retentionAmountS3: retentionCountSchema.default(0),
    retentionDaysS3: retentionCountSchema.default(0),
    retentionMaxStorageGbS3: retentionGbSchema.default(0),
    /** Dispatch a first run right after creating the schedule. */
    backupNow: z.boolean().default(false),
  })
  .refine(s3Rule, {
    message: 's3StorageUuid is required when saveS3 is enabled',
    path: ['s3StorageUuid'],
  });
export type CreateBackupScheduleInput = z.infer<typeof createBackupScheduleSchema>;

export const updateBackupScheduleSchema = z
  .object(
    Object.fromEntries(
      Object.entries(scheduleShape).map(([key, schema]) => [key, schema.optional()]),
    ) as { [K in keyof typeof scheduleShape]: z.ZodOptional<(typeof scheduleShape)[K]> },
  )
  .refine((data) => data.saveS3 !== true || typeof data.s3StorageUuid === 'string', {
    message: 's3StorageUuid is required when enabling saveS3',
    path: ['s3StorageUuid'],
  });
export type UpdateBackupScheduleInput = z.infer<typeof updateBackupScheduleSchema>;

// ── S3 destination registry (admin settings) ────────────────────────

/**
 * Coolify's API has no S3-storage CRUD or list — storages are created in
 * Coolify's UI. Admins register the resulting uuids here (with a label)
 * so backup forms can offer them; the panel never sees the credentials.
 */
export const s3StorageEntrySchema = z.object({
  uuid: z.string().trim().min(1).max(100),
  label: z.string().trim().min(1).max(200),
});
export type S3StorageEntry = z.infer<typeof s3StorageEntrySchema>;

export const backupsSettingsSchema = z.object({
  s3Storages: z
    .array(s3StorageEntrySchema)
    .max(50)
    .refine(
      (list) => new Set(list.map((s) => s.uuid)).size === list.length,
      'Duplicate S3 storage uuid',
    ),
});
export type BackupsSettingsInput = z.infer<typeof backupsSettingsSchema>;

// ── Per-plan default backup policy (ADR-0015) ───────────────────────

/**
 * Applied automatically when a database is provisioned into a subscription
 * whose plan includes the `backups` feature. A plan without a stored
 * policy uses BUILTIN_BACKUP_POLICY; `autoApply: false` opts a plan out of
 * automatic schedules without removing the feature.
 */
export const backupPlanPolicySchema = z
  .object({
    autoApply: z.boolean().default(true),
    ...scheduleShape,
    enabled: scheduleShape.enabled.default(true),
    saveS3: scheduleShape.saveS3.default(false),
    dumpAll: scheduleShape.dumpAll.default(false),
    retentionAmountLocally: retentionCountSchema.default(7),
    retentionDaysLocally: retentionCountSchema.default(0),
    retentionMaxStorageGbLocally: retentionGbSchema.default(0),
    retentionAmountS3: retentionCountSchema.default(0),
    retentionDaysS3: retentionCountSchema.default(0),
    retentionMaxStorageGbS3: retentionGbSchema.default(0),
  })
  .refine(s3Rule, {
    message: 's3StorageUuid is required when saveS3 is enabled',
    path: ['s3StorageUuid'],
  });
export type BackupPlanPolicy = z.infer<typeof backupPlanPolicySchema>;

/**
 * The conservative built-in default: daily at 03:00 (server time of the
 * database's host), keep the last 7 dumps locally, no S3 (an S3
 * destination cannot be assumed — it requires a uuid registered by the
 * admin).
 */
export const BUILTIN_BACKUP_POLICY: BackupPlanPolicy = {
  autoApply: true,
  enabled: true,
  frequency: '0 3 * * *',
  saveS3: false,
  s3StorageUuid: null,
  dumpAll: false,
  retentionAmountLocally: 7,
  retentionDaysLocally: 0,
  retentionMaxStorageGbLocally: 0,
  retentionAmountS3: 0,
  retentionDaysS3: 0,
  retentionMaxStorageGbS3: 0,
};

// ── DTOs ────────────────────────────────────────────────────────────

/**
 * One scheduled backup as read live from Coolify (never stored panel-side).
 * `s3StorageId` is Coolify's internal integer id — the API never returns
 * the uuid and offers no way to map between them, so this is displayed as
 * an opaque reference only.
 */
export interface BackupScheduleDto {
  uuid: string;
  enabled: boolean;
  frequency: string;
  saveS3: boolean;
  s3StorageId: number | null;
  dumpAll: boolean;
  retentionAmountLocally: number;
  retentionDaysLocally: number;
  retentionMaxStorageGbLocally: number;
  retentionAmountS3: number;
  retentionDaysS3: number;
  retentionMaxStorageGbS3: number;
  createdAt: string | null;
}

export const BACKUP_EXECUTION_STATUSES = ['success', 'failed', 'running'] as const;
export type BackupExecutionStatus = (typeof BACKUP_EXECUTION_STATUSES)[number] | 'unknown';

/** One backup run, newest first. */
export interface BackupExecutionDto {
  uuid: string;
  status: BackupExecutionStatus;
  /** Dump file path on the database's server. */
  filename: string | null;
  /** Bytes; null when Coolify did not report a size. */
  sizeBytes: number | null;
  /** Error output for failed runs — the failure surfacing channel. */
  message: string | null;
  createdAt: string | null;
}

/** GET /api/backups/databases/:id */
export interface DatabaseBackupsDto {
  resourceId: string;
  subscriptionId: string;
  /** The plan's `backups` feature — mutations are gated on it. */
  featureEnabled: boolean;
  /** False for engines Coolify cannot dump (redis, clickhouse, …). */
  engineSupported: boolean;
  schedules: BackupScheduleDto[];
  /** Coolify read failure, surfaced instead of a hard error. */
  liveError: string | null;
  s3Storages: S3StorageEntry[];
  coolifyBaseUrl: string | null;
}

/** One database's slice of the per-subscription rollup. */
export interface SubscriptionBackupDatabaseDto {
  resourceId: string;
  name: string;
  coolifyUuid: string | null;
  /** False for engines Coolify cannot dump (redis, clickhouse, …). */
  engineSupported: boolean;
  schedules: BackupScheduleDto[];
  /** Most recent executions across the database's schedules, newest first. */
  executions: BackupExecutionDto[];
  /** Coolify read failure for this database, if any. */
  error: string | null;
}

/** GET /api/backups/subscriptions/:id */
export interface SubscriptionBackupsDto {
  subscriptionId: string;
  featureEnabled: boolean;
  databases: SubscriptionBackupDatabaseDto[];
}

/** GET /api/backups/plan-policies */
export interface BackupPlanPolicyDto {
  planId: string;
  planName: string;
  /** Whether the plan carries the `backups` feature. */
  featureEnabled: boolean;
  /** Stored policy, or null when the built-in default applies. */
  policy: BackupPlanPolicy | null;
}

// ── Subscription backup sets (V2-G6, ADR-0042) ────────────────────

/** Module-owned plan descriptors controlling tenant backup actions. */
export const BACKUPS_RETAINED_SETS_LIMIT = 'backups_retained_sets';
export const BACKUPS_SCHEDULE_FEATURE = 'backups_schedule';
export const BACKUPS_TRIGGER_FEATURE = 'backups_trigger';
export const BACKUPS_INSPECT_FEATURE = 'backups_inspect';
export const BACKUPS_RESTORE_REQUEST_FEATURE = 'backups_restore_request';

/** V2-G14 requires a successful staging restore no older than this. */
export const BACKUP_RESTORE_TEST_MAX_AGE_DAYS = 30;

export const BACKUP_DESTINATION_TYPES = ['local', 's3', 'sftp'] as const;
export type BackupDestinationType = (typeof BACKUP_DESTINATION_TYPES)[number];

export const backupRetentionSchema = z.object({
  keepLast: z.number().int().min(1).max(10_000).default(7),
  keepHourly: z.number().int().min(0).max(10_000).default(0),
  keepDaily: z.number().int().min(0).max(10_000).default(7),
  keepWeekly: z.number().int().min(0).max(10_000).default(4),
  keepMonthly: z.number().int().min(0).max(10_000).default(12),
  keepYearly: z.number().int().min(0).max(10_000).default(3),
});
export type BackupRetention = z.infer<typeof backupRetentionSchema>;

const repositorySchema = z.string().trim().min(1).max(2_000);

function repositoryMatchesType(input: {
  type: BackupDestinationType;
  repository: string;
}): boolean {
  if (input.type === 'local') {
    return (
      input.repository.startsWith('/var/lib/cpm-backups/repositories/')
      && !input.repository.includes('..')
    );
  }
  if (input.type === 's3') return /^s3:(?:https?:\/\/)?[^ \r\n]+$/.test(input.repository);
  return /^sftp:[^ \r\n]+$/.test(input.repository);
}

const destinationBaseSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    type: z.enum(BACKUP_DESTINATION_TYPES),
    /**
     * Local repositories are bound to one Coolify server. Remote repositories
     * may be global (`null`) or constrained to a server.
     */
    serverUuid: z.string().trim().min(1).max(200).nullish(),
    /** Restic repository URI. It never contains a password or secret key. */
    repository: repositorySchema,
    retention: backupRetentionSchema.default({}),
    bandwidthLimitKib: z.number().int().min(0).max(10_000_000).default(0),
    enabled: z.boolean().default(true),
  })
  .superRefine((input, ctx) => {
    if (!repositoryMatchesType(input)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['repository'],
        message:
          input.type === 'local'
            ? 'Local repositories must be below /var/lib/cpm-backups/repositories'
            : `Repository must use the ${input.type}: restic backend`,
      });
    }
    if (input.type === 'local' && !input.serverUuid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serverUuid'],
        message: 'A local destination must name its Coolify server',
      });
    }
  });

export const createBackupDestinationSchema = destinationBaseSchema.and(
  z.object({
    /** Restic repository password; write-only and required on create. */
    repositoryPassword: z.string().min(12).max(10_000),
    /**
     * Separate 32-byte metadata key, base64-encoded. Operators must escrow it
     * independently from the panel encryption key and repository password.
     */
    metadataKey: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9+/]{43}=$/, 'Metadata key must be a base64-encoded 32-byte value'),
    /**
     * Backend credential material (JSON or an OpenSSH private key), sent to
     * the destination server over SSH stdin and never returned by the API.
     */
    transportSecret: z.string().max(100_000).nullish(),
  }),
);
export type CreateBackupDestinationInput = z.infer<typeof createBackupDestinationSchema>;

export const updateBackupDestinationSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  serverUuid: z.string().trim().min(1).max(200).nullable().optional(),
  retention: backupRetentionSchema.optional(),
  bandwidthLimitKib: z.number().int().min(0).max(10_000_000).optional(),
  enabled: z.boolean().optional(),
  repositoryPassword: z.string().min(12).max(10_000).optional(),
  metadataKey: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9+/]{43}=$/, 'Metadata key must be a base64-encoded 32-byte value')
    .optional(),
  transportSecret: z.string().max(100_000).nullable().optional(),
});
export type UpdateBackupDestinationInput = z.infer<typeof updateBackupDestinationSchema>;

export type BackupKeyStatus = 'available' | 'missing' | 'unreadable';

export interface BackupDestinationDto {
  id: string;
  name: string;
  type: BackupDestinationType;
  serverUuid: string | null;
  repository: string;
  retention: BackupRetention;
  bandwidthLimitKib: number;
  enabled: boolean;
  repositoryPasswordStatus: BackupKeyStatus;
  metadataKeyStatus: BackupKeyStatus;
  transportSecretStatus: BackupKeyStatus;
  createdAt: string;
  updatedAt: string;
}

export const createSubscriptionBackupScheduleSchema = z.object({
  quiesceApplicationIds: z.array(z.string().uuid()).max(10)
    .refine((ids) => new Set(ids).size === ids.length, 'Duplicate application id')
    .default([]),
  frequency: backupFrequencySchema,
  enabled: z.boolean().default(true),
  destinationIds: z
    .array(z.string().uuid())
    .min(1)
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, 'Duplicate destination id'),
});
export const updateSubscriptionBackupScheduleSchema =
  createSubscriptionBackupScheduleSchema.partial();
export type CreateSubscriptionBackupScheduleInput = z.infer<
  typeof createSubscriptionBackupScheduleSchema
>;
export type UpdateSubscriptionBackupScheduleInput = z.infer<
  typeof updateSubscriptionBackupScheduleSchema
>;

export interface SubscriptionBackupScheduleDto {
  quiesceApplicationIds: string[];
  id: string;
  subscriptionId: string;
  frequency: string;
  enabled: boolean;
  destinationIds: string[];
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const BACKUP_SET_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'partial',
  'failed',
  'pruned',
] as const;
export type BackupSetStatus = (typeof BACKUP_SET_STATUSES)[number];

export const BACKUP_COMPONENT_KINDS = [
  'volume',
  'database',
  'metadata',
  'mail',
  'dns',
  'file_access',
] as const;
export type BackupComponentKind = (typeof BACKUP_COMPONENT_KINDS)[number];
export type BackupComponentStatus = 'pending' | 'succeeded' | 'failed' | 'skipped';

export interface BackupDatabaseRowCount {
  table: string;
  rows: number;
}

export interface BackupComponentDto {
  kind: BackupComponentKind;
  resourceId: string | null;
  name: string;
  serverUuid: string;
  status: BackupComponentStatus;
  path: string | null;
  sha256: string | null;
  sizeBytes: number | null;
  engine: string | null;
  rowCounts: BackupDatabaseRowCount[];
  message: string | null;
}

export interface BackupRepositorySnapshotDto {
  destinationId: string;
  serverUuid: string;
  snapshotId: string | null;
  status: 'succeeded' | 'failed';
  bytesAdded: number | null;
  message: string | null;
}

export interface SubscriptionBackupManifestDto {
  /** Frozen when queued; omitted in historical sets. */
  quiesceApplicationIds?: string[];
  applicationCaptures?: Array<{
    resourceId: string;
    serverUuid: string;
    sqliteFiles: number;
    downtimeMs: number;
  }>;
  version: 1;
  subscriptionId: string;
  components: BackupComponentDto[];
  repositories: BackupRepositorySnapshotDto[];
  embeddedServiceWarnings: string[];
}

export interface SubscriptionBackupSetDto {
  id: string;
  subscriptionId: string;
  scheduleId: string | null;
  status: BackupSetStatus;
  attempt: number;
  retryOfId: string | null;
  requestedBy: string | null;
  manifest: SubscriptionBackupManifestDto;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export const BACKUP_RESTORE_STATUSES = [
  'queued',
  'running',
  'verifying',
  'awaiting_promotion',
  'promoting',
  'promoted',
  'failed',
  'cancelled',
] as const;
export type BackupRestoreStatus = (typeof BACKUP_RESTORE_STATUSES)[number];

export const requestBackupRestoreSchema = z.object({
  backupSetId: z.string().uuid(),
  /** Every restore is isolated unless an admin later promotes it. */
  targetMode: z.literal('staging').default('staging'),
  targetServerUuid: z.string().trim().min(1).max(200),
  targetProjectUuid: z.string().trim().min(1).max(200).nullish(),
  healthCheckUrls: z.array(z.string().url().max(2_000)).max(25).default([]),
});
export type RequestBackupRestoreInput = z.infer<typeof requestBackupRestoreSchema>;

export const promoteBackupRestoreSchema = z.object({
  confirmation: z.literal('PROMOTE'),
  /**
   * A fresh successful backup of production, created after this restore was
   * requested, is mandatory before destructive promotion.
   */
  prePromotionBackupSetId: z.string().uuid(),
});
export type PromoteBackupRestoreInput = z.infer<typeof promoteBackupRestoreSchema>;

export interface BackupRestoreVerificationDto {
  hashesVerified: boolean;
  databasesVerified: boolean;
  rowCountsVerified: boolean;
  healthChecksVerified: boolean;
  checkedComponents: number;
  message: string | null;
}

export interface BackupRestoreWorkflowDto {
  id: string;
  subscriptionId: string;
  backupSetId: string;
  status: BackupRestoreStatus;
  targetMode: 'staging';
  targetServerUuid: string;
  targetProjectUuid: string | null;
  stagingPath: string | null;
  healthCheckUrls: string[];
  verification: BackupRestoreVerificationDto | null;
  requestedBy: string | null;
  promotedBy: string | null;
  error: string | null;
  startedAt: string | null;
  verifiedAt: string | null;
  promotedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BackupPermissionsDto {
  schedule: boolean;
  trigger: boolean;
  inspect: boolean;
  requestRestore: boolean;
  promote: boolean;
}

export interface SubscriptionProtectionDto {
  applications?: Array<{ id: string; name: string }>;
  subscriptionId: string;
  featureEnabled: boolean;
  permissions: BackupPermissionsDto;
  schedules: SubscriptionBackupScheduleDto[];
  sets: SubscriptionBackupSetDto[];
  restores: BackupRestoreWorkflowDto[];
  recentSuccessfulRestoreTestAt: string | null;
  restoreTestRequiredAfter: string;
  embeddedServiceWarnings: string[];
}
